<template>
  <div class="text-neutral-500">
    <p class="text-center text-sm font-normal leading-normal mb-6">
      {{ $t('modal.pin.subtitle') }}
      <br>
      <span class="font-medium cursor-pointer" @click="$emit('not_found')">+{{ account.phone.calling_code }} {{ account.phone.phone }}</span>
    </p>

    <div class="flex flex-col w-full items-center no-ring-form">
      <div class="w-full max-w-32">
        <UInput
          v-model="bindModal"
          :class="[
            'pin-input text-3xl tracking-widest'
          ]"
          :disabled="disabled || modelValue.loading"
          :inputClass="`!pl-10 ${modelValue.error ? '!border-red' : ''}`"
          autocomplete="one-time-code"
          inputmode="numeric"
          maxlength="4"
          placeholder="0000"
          type="text"
          @beforeinput="handleBeforeInput"
          @input="handleInput"
        />
      </div>

      <div v-if="modelValue.error && !modelValue.loading" class="mb-2 mt-4">
        <p class="text-red text-center">{{ modelValue.error ? $t(modelValue.error) : "&nbsp" }}</p>
      </div>

      <BaseIcon v-if="modelValue.loading" class="mt-4" name="loading"/>

      <p v-if="!modelValue.loading" class="flex flex-col items-center justify-center text-sm font-normal leading-normal mt-4">
        <span v-if="!disabled" class="flex items-center justify-center ml-1">
          {{ $t('modal.pin.not_received') }}
          {{ $t('modal.pin.resend') }}
          <Timer
              :limit="account.authentication.expiration"
              classes="ml-1 mt-px font-medium text-neutral-700 min-w-max text-center"
              @ended="ended"
          />
        </span>

        <RecaptchaV2 v-show="disabled && !isWhiteListCountry"
                     :class="[captchaError ? 'border-red border-2' : '']"
                     class="mb-4"
                     @widget-id="handleWidgetId"
                     @load-callback="handleLoadCallback" />

        <button v-if="disabled"
                class="text-sm ml-1 -mt-px font-medium text-neutral underline transition-all hover:cursor-pointer hover:text-neutral-700"
                @click="refreshPin">
          {{ $t('modal.pin.resend_btn') }}
        </button>
      </p>

    </div>

  </div>
</template>

<script lang="ts" setup>
import {useAccountStore} from "~/store/account"
import {RecaptchaV2, useRecaptcha} from "vue3-recaptcha-v2"

const emit = defineEmits(['not_found','unregistered','continue'])
const modelValue = useAttrs().modelValue as ModalModel
modelValue.icon="lock"
modelValue.title="modal.pin.title"

const account = useAccountStore()
const { handleGetResponse } = useRecaptcha()
const {locale: activeLocale} = useI18n()

const bindModal = ref('')
const disabled = ref<boolean>(false)
const captchaToken = ref<string | null>(null)
const captchaError = ref<string | null>(null)
const isWhiteListCountry = (['PT', 'ES', 'FR', 'CH', 'LU'].includes(<string>account.phone.country))

function handleBeforeInput(event: InputEvent): void {
 if (event.data && !/\d/.test(event.data)) {
   event.preventDefault()
 }
}

function handleInput(event: Event): void {
 const target = event.target as HTMLInputElement
 const value = target.value.replace(/\D/g, '').slice(0, 4)
 bindModal.value = value

 if (value.length === 4) {
   handleOnComplete(value)
 }
}

async function handleOnComplete(value: string) {
  if(modelValue.loading || value.length !== 4) return

  account.authentication.pin = value
  modelValue.loading = true
  const result = await account.verifyPin()
  modelValue.loading = false

  if (isNuxtError(result)) {
    if (result.statusCode === 500) return emit('not_found')
    return modelValue.error = result.message
  }

  if (!account.registered) return emit('unregistered')

  return emit('continue')
}

function ended() {
  modelValue.error = 'modal.pin.expired'
  disabled.value = true
}

async function refreshPin() {
  if (!captchaToken.value && !isWhiteListCountry) {
    captchaError.value = 'modal.pin.captcha_error'
    return
  }

  const result = await account.refreshPin(activeLocale.value, captchaToken.value)

  bindModal.value = ''

  if (isNuxtError(result)) {
    modelValue.error = result.message
    return
  }

  disabled.value = false
  modelValue.error = null
  modelValue.loading = false
}

const handleWidgetId = (widgetId: number) => {
  handleGetResponse(widgetId)
}

const handleLoadCallback = (response: unknown) => {
  captchaToken.value = response as string
  captchaError.value = null
}
</script>
